import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { endpoints } from "@/lib/api";
import { Upload, FileText } from "lucide-react";
import { toast } from "sonner";

const TYPES = ["Policy", "Procedure", "Work Instruction", "Form", "Manual", "Register", "External"];

export default function DocumentUploadModal({ open, clients, defaultClientSlug, onOpenChange, onCreated }) {
  const [consultants, setConsultants] = useState([]);
  const [form, setForm] = useState({
    client_slug: defaultClientSlug || "", title: "", doc_type: "Procedure", doc_number: "",
    owner: "", owner_email: "", approval_workflow: "two_step",
    revision: "1", change_notes: "", uploaded_by: "",
  });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();

  useEffect(() => {
    if (open) {
      setForm({
        client_slug: defaultClientSlug || "", title: "", doc_type: "Procedure", doc_number: "",
        owner: "", owner_email: "", approval_workflow: "two_step",
        revision: "1", change_notes: "", uploaded_by: "",
      });
      setFile(null);
      endpoints.listConsultants().then(setConsultants).catch(() => {});
      endpoints.me().then((u) => setForm((f) => ({ ...f, uploaded_by: f.uploaded_by || u.name || "" }))).catch(() => {});
    }
  }, [open, defaultClientSlug]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async () => {
    if (!form.client_slug) { toast.error("Pick a client"); return; }
    if (!form.title) { toast.error("Title is required"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v ?? ""));
      if (file) fd.append("file", file);
      await endpoints.createDocument(fd);
      toast.success("Document created");
      onCreated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create document");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="doc-upload-modal" className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileText className="h-5 w-5 text-indigo-600" /> New controlled document</DialogTitle>
          <DialogDescription>Register a new document. Upload the current file and set the metadata.</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2">
            <Label>Client *</Label>
            <Select value={form.client_slug} onValueChange={(v) => set("client_slug", v)}>
              <SelectTrigger data-testid="doc-upload-client" className="h-9"><SelectValue placeholder="Select client" /></SelectTrigger>
              <SelectContent>
                {(clients || []).map((c) => <SelectItem key={c.slug} value={c.slug}>{c.company_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="sm:col-span-2">
            <Label>Document title *</Label>
            <Input data-testid="doc-upload-title" value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. Quality Manual" />
          </div>

          <div>
            <Label>Type</Label>
            <Select value={form.doc_type} onValueChange={(v) => set("doc_type", v)}>
              <SelectTrigger data-testid="doc-upload-type" className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                {TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Document number <span className="text-slate-400 text-[10px]">(auto if blank)</span></Label>
            <Input data-testid="doc-upload-number" value={form.doc_number} onChange={(e) => set("doc_number", e.target.value)} placeholder="e.g. PRO-001" />
          </div>

          <div>
            <Label>Owner</Label>
            <Select value={form.owner} onValueChange={(v) => {
              set("owner", v);
              const c = consultants.find((cc) => cc.name === v);
              if (c) set("owner_email", c.email);
            }}>
              <SelectTrigger data-testid="doc-upload-owner" className="h-9"><SelectValue placeholder="Pick owner" /></SelectTrigger>
              <SelectContent>
                {consultants.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Owner email</Label>
            <Input data-testid="doc-upload-owner-email" value={form.owner_email} onChange={(e) => set("owner_email", e.target.value)} placeholder="owner@example.com" />
          </div>

          <div>
            <Label>Approval workflow</Label>
            <Select value={form.approval_workflow} onValueChange={(v) => set("approval_workflow", v)}>
              <SelectTrigger data-testid="doc-upload-workflow" className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="single_step">Single step (uploader approves)</SelectItem>
                <SelectItem value="two_step">Two step (separate approver)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Revision</Label>
            <Input data-testid="doc-upload-revision" value={form.revision} onChange={(e) => set("revision", e.target.value)} placeholder="1" />
          </div>

          <div className="sm:col-span-2">
            <Label>Change notes</Label>
            <Textarea data-testid="doc-upload-notes" value={form.change_notes} onChange={(e) => set("change_notes", e.target.value)} rows={2} placeholder="Initial issue" />
          </div>

          <div className="sm:col-span-2">
            <Label>Performing this action as</Label>
            <Select value={form.uploaded_by} onValueChange={(v) => set("uploaded_by", v)}>
              <SelectTrigger data-testid="doc-upload-actor" className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                {consultants.map((c) => <SelectItem key={c.id} value={c.name}>{c.name} — {c.role}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="sm:col-span-2">
            <Label>Document file</Label>
            <div
              onClick={() => fileRef.current?.click()}
              className="mt-1 rounded-2xl border-2 border-dashed border-slate-300 dark:border-white/15 hover:border-indigo-400 p-6 text-center cursor-pointer transition-colors"
              data-testid="doc-upload-dropzone"
            >
              <Upload className="h-6 w-6 mx-auto text-slate-400 mb-2" />
              {file ? (
                <div className="text-sm font-medium">{file.name} <span className="text-xs text-slate-500">({Math.round(file.size / 1024)} KB)</span></div>
              ) : (
                <>
                  <div className="text-sm font-medium">Click to upload any file type</div>
                  <div className="text-[11px] text-slate-500">PDF, Word, Excel, images — up to 25 MB</div>
                </>
              )}
              <input ref={fileRef} type="file" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="doc-upload-submit" className="brand-gradient text-white" disabled={busy} onClick={onSubmit}>
            {busy ? "Creating..." : "Create document"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
